/**
 * DangoteEvidenceVault — Evidence linked to monthly fields and calculations.
 * Route: /corporate/evidence-vault
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDangote } from "@/context/DangoteContext";
import { PageHeader, SectionCard } from "@/components/ds";
import { CheckCircle2, AlertTriangle, Clock, Upload, ChevronRight, Shield } from "lucide-react";

const EVIDENCE_REQUIREMENTS = {
  cement: [
    { field: "coal_t",              label: "Coal intake records",            required: true,  tier: "T2", docs: "Weighbridge records, purchase invoices" },
    { field: "clinker_production_t",label: "Clinker production log",         required: true,  tier: "T1", docs: "Kiln control system / production report" },
    { field: "cement_production_t", label: "Cement dispatch records",        required: true,  tier: "T1", docs: "Sales/dispatch data, silo meters" },
    { field: "electricity_kwh",     label: "Electricity meter records",      required: true,  tier: "T1", docs: "Utility bill or metered consumption" },
    { field: "afr_t",               label: "AFR intake and analysis",        required: false, tier: "T2", docs: "Gate receipts + waste acceptance docs + LHV analysis" },
    { field: "baseline_disposal",   label: "Baseline scenario documentation",required: true,  tier: "T3", docs: "Baseline period actuals or benchmark justification" },
  ],
  refinery: [
    { field: "crude_processed_bbl", label: "Crude throughput records",       required: true,  tier: "T1", docs: "Metered crude intake records" },
    { field: "flared_gas_kNm3",     label: "Flare meter records",            required: true,  tier: "T2", docs: "Flare stack meter or CEMS data" },
    { field: "electricity_kwh",     label: "Electricity consumption",        required: true,  tier: "T1", docs: "Utility bill or plant meter" },
  ],
  fertilizer: [
    { field: "urea_production_t",   label: "Urea production records",        required: true,  tier: "T1", docs: "Production log, batch records" },
    { field: "natural_gas_t",       label: "Natural gas metering",           required: true,  tier: "T1", docs: "Gas meter records, utility invoice" },
    { field: "co2_captured_t",      label: "CO₂ capture records",           required: false, tier: "T2", docs: "Process CO₂ meter + utilization records" },
  ],
  sugar: [
    { field: "sugarcane_t",         label: "Cane intake records",            required: true,  tier: "T1", docs: "Weighbridge + cane supplier records" },
    { field: "sugar_production_t",  label: "Sugar production log",           required: true,  tier: "T1", docs: "Production batch records" },
  ],
  logistics: [
    { field: "fleet_km",            label: "Fleet GPS/telematics records",   required: true,  tier: "T1", docs: "GPS telematics or fuel card system" },
    { field: "diesel_t",            label: "Fuel consumption records",       required: true,  tier: "T1", docs: "Fuel card records + depot tank gauges" },
  ],
};

const TIER_COLORS = {
  T1: "bg-emerald-100 text-emerald-700 border-emerald-200",
  T2: "bg-teal-100 text-teal-700 border-teal-200",
  T3: "bg-amber-100 text-amber-700 border-amber-200",
};

function EvidenceRow({ item, onUpload }) {
  const [uploaded, setUploaded] = useState(false);
  const StatusIcon = uploaded ? CheckCircle2 : item.required ? AlertTriangle : Clock;
  const statusColor = uploaded ? "text-emerald-500" : item.required ? "text-amber-500" : "text-slate-400";
  const statusLabel = uploaded ? "Uploaded" : item.required ? "Required" : "Optional";

  const handleToggle = () => {
    const next = !uploaded;
    setUploaded(next);
    onUpload && onUpload(next ? 1 : -1);
  };

  return (
    <div className="flex items-start justify-between py-3 border-b border-slate-50 last:border-0">
      <div className="flex-1 min-w-0 mr-3">
        <div className="flex items-center gap-2 mb-0.5">
          <StatusIcon size={11} className={`flex-shrink-0 ${statusColor}`} />
          <p className="text-xs font-semibold text-slate-900">{item.label}</p>
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${TIER_COLORS[item.tier]}`}>{item.tier}</span>
          {item.required && !uploaded && (
            <span className="text-[9px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">Required</span>
          )}
        </div>
        <p className="text-xs text-slate-400 ml-4">{item.docs}</p>
        <p className="text-[9px] text-slate-400 font-mono ml-4 mt-0.5">Field: {item.field}</p>
      </div>
      <button onClick={handleToggle}
        className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors flex-shrink-0 ${
          uploaded
            ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
            : "bg-white text-slate-600 border-slate-200 hover:border-teal-400"
        }`}>
        {uploaded ? <><CheckCircle2 size={10}/> Uploaded</> : <><Upload size={10}/> Upload</>}
      </button>
    </div>
  );
}

export default function DangoteEvidenceVault() {
  const navigate = useNavigate();
  const { period, byBu, selectedPeriod } = useDangote();
  const [selectedAssetType, setSelectedAssetType] = useState("cement");

  const requirements = EVIDENCE_REQUIREMENTS[selectedAssetType] || [];
  const required = requirements.filter(r => r.required).length;
  const [uploadedCount, setUploadedCount] = useState(0);
  const completeness = requirements.length > 0 ? Math.round((uploadedCount / requirements.length) * 100) : 0;
  const requiredCompleteness = required > 0 ? Math.round((Math.min(uploadedCount, required) / required) * 100) : 0;

  const BU_TYPES = [
    { type: "cement", label: "Cement" },
    { type: "refinery", label: "Refinery" },
    { type: "fertilizer", label: "Fertilizer" },
    { type: "sugar", label: "Sugar" },
    { type: "logistics", label: "Logistics" },
  ];

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Dangote Industries</p>
          <PageHeader title="Evidence Vault" />
          <p className="text-xs text-slate-400 mt-0.5">
            Evidence linked to monthly data fields and calculations — {period?.label}
          </p>
        </div>
      </div>

      {/* Clarity block — same as Oko-Oba Evidence Vault */}
      <div className="bg-teal-50 border border-teal-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <Shield size={16} className="text-teal-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-teal-800 mb-2">Evidence Requirements — Important Distinctions</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              {[
                ["T1 — Measured", "Directly metered/measured data (weighbridge, gas meter, electricity meter)"],
                ["T2 — Calculated", "Derived from measurements with documented calculation method"],
                ["T3 — Estimated", "Estimated from default factors or benchmarks — weakest evidence tier"],
              ].map(([l,d]) => (
                <div key={l} className="bg-white/60 rounded-lg px-3 py-2">
                  <p className="font-bold text-teal-700">{l}</p>
                  <p className="text-teal-600">{d}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-amber-700 font-semibold mt-2">
              Evidence coverage affects VRS score and is required for VVB validation engagement.
            </p>
          </div>
        </div>
      </div>

      {/* BU selector */}
      <div className="flex items-center gap-2 flex-wrap">
        {BU_TYPES.map(bt => (
          <button key={bt.type} onClick={() => setSelectedAssetType(bt.type)}
            className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-all ${
              selectedAssetType === bt.type
                ? "bg-[#0F172A] text-white border-transparent"
                : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
            }`}>
            {bt.label}
          </button>
        ))}
      </div>

      {/* Evidence requirements */}
      <SectionCard>
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">
            {BU_TYPES.find(b => b.type === selectedAssetType)?.label} — Required Evidence
          </p>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">{required} required · {requirements.length - required} optional</span>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
              completeness >= 100 ? "bg-emerald-100 text-emerald-700 border-emerald-200"
              : "bg-amber-100 text-amber-700 border-amber-200"
            }`} data-testid="evidence-completeness">
              {completeness}% uploaded
            </span>
          </div>
        </div>
        {completeness < 100 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 flex items-center gap-2">
            <AlertTriangle size={12} className="text-amber-500 flex-shrink-0" />
            <p className="text-xs text-amber-700 font-semibold">
              Evidence completeness: {completeness}% — Insufficient for external verification
            </p>
          </div>
        )}
        <div>
          {requirements.map(item => (
            <EvidenceRow key={item.field} item={item} onUpload={(delta) => setUploadedCount(prev => Math.max(0, prev + delta))} />
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100 bg-slate-50 rounded-lg px-3 py-2">
          <p className="text-xs font-bold text-slate-600 mb-1">Evidence Completeness</p>
          <p className="text-xs text-slate-500">
            Upload evidence documents above to improve data confidence score and VRS.
            Critical (T1) evidence is required before period approval for external verification.
          </p>
        </div>
      </SectionCard>

      {/* Action */}
      <SectionCard>
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Next Actions</p>
        {[
          ["Monthly Reporting", "/corporate/reporting"],
          ["Carbon Strategy",   "/corporate/carbon-strategy"],
          ["Reports",           "/corporate/reports"],
        ].map(([l,p]) => (
          <button key={p} onClick={() => navigate(p)}
            className="w-full flex items-center justify-between py-2 text-xs text-slate-600 hover:text-teal-700 group">
            {l} <ChevronRight size={11} className="text-slate-300 group-hover:text-teal-500" />
          </button>
        ))}
      </SectionCard>
    </div>
  );
}
